import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {useObserver} from "mobx-react-lite";
import {useHistory} from "react-router-dom";
import {Col, Row, Input, DatePicker, Tabs, Radio} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../hooks/useStore";
import {useFetch} from "../../hooks/useFetch";
import useIsFocused from "../../hooks/useIsFocused";
import {OrderTable} from "./OrderTable";
import {SelectField} from "./orderPageStyles";
import {isConstantExist, arrayAlphabeticalSort, normalizeText} from "../../utils/helpers";
import {cities, deliveryStatuses} from "../../constants/constants";
import moment from "moment";
import {
    CityFilter,
    CompanyFilter,
    PoolIntegrationTypeFilter
} from "../_defaultComponents/FilterComponents";
import {WarningOutlined} from "@ant-design/icons";

const {TabPane} = Tabs;
export const OrderPage = () => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["orderPage", "deliveryStatuses"]);
    const isFocused = useIsFocused();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [courierText, setCourierText] = useState("");
    const [text, setText] = useState("");
    const [call, setCall] = useState(false);
    const [field, setField] = useState({field: "", asc: true});
    const [sortField, setSortField] = useState({field: "", asc: true});
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    const [poolGroups, setPoolGroups] = useState([]);
    const [workType, setWorkType] = useState("all");
    const [courierSearchText, setCourierSearchText] = useState("");
    const [selectedCities, setSelectedCities] = useState([]);
    const [integrationType, setIntegrationType] = useState("");
    recallRef.current = recall;
    const filters = stores.meStore.orderPage?.filters;
    const filterStartDate = filters.dateRange.length > 0 ? filters.dateRange[0] : undefined;
    const filterEndDate = filters.dateRange.length > 0 ? filters.dateRange[1] : undefined;
    const defaultDateRange = [moment().subtract(30, "days"), moment()];
    const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companies: filters.companies.join("."),
            selectedCities: selectedCities.join(","),
            limit: 1000
        }),
        dependencies: [filters.companies, selectedCities],
        initialCall:
            stores.meStore.checkRole("isCompanyUser") ||
            stores.meStore.orderPage?.filters.companies?.length > 0
    });

    //@TODO: we do not need to 2 request for this process. Delete one!
    const [orderListLoading, orderList, setOrderList, setOrderListLoading] = useFetch({
        fetchFn: stores.orderStore.getOrderList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            regionGroups: filters.regionGroups,
            companies: filters.companies,
            subcompanies: filters.selectedSubCompanies,
            selectedCities: selectedCities,
            deliveryStatus: filters.orderType,
            searchText,
            courierName: normalizeText(courierSearchText),
            sortField,
            deliveryStatusOverrides: filters.deliveryStatusOverrides,
            groups: filters.upperGroup,
            startDate:
                filters.orderType === "past" && !filterStartDate
                    ? selectedDateRange[0]
                    : filterStartDate,
            endDate:
                filters.orderType === "past" && !filterEndDate
                    ? selectedDateRange[1]
                    : filterEndDate,
            poolIds: poolGroups,
            workType: workType,
            integrationType: integrationType
        }),
        dependencies: [
            currentPage,
            selectedCities,
            filters.companies,
            filters.selectedSubCompanies,
            filters.regionGroups,
            searchText,
            courierSearchText,
            call,
            filters.orderType,
            sortField,
            filters.deliveryStatusOverrides,
            filters.upperGroup,
            filterStartDate,
            filterEndDate,
            JSON.stringify(poolGroups),
            workType,
            integrationType,
            selectedDateRange
        ],
        initialCall: false,
        waitForResponse: true,
        callbackFunc: data => setRecallList(data)
    });

    const [recallListLoading, recallList, setRecallList, setRecallListLoading] = useFetch({
        fetchFn: stores.orderStore.getOrderList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            regionGroups: filters.regionGroups,
            companies: filters.companies,
            subcompanies: filters.selectedSubCompanies,
            selectedCities: selectedCities,
            deliveryStatus: filters.orderType,
            searchText,
            courierName: normalizeText(courierSearchText),
            sortField,
            deliveryStatusOverrides: filters.deliveryStatusOverrides,
            groups: filters.upperGroup,
            startDate:
                filters.orderType === "past" && !filterStartDate
                    ? moment()
                          .subtract(30, "days")
                          .startOf("day")
                          .toDate()
                    : filterStartDate,
            endDate:
                filters.orderType === "past" && !filterEndDate
                    ? moment()
                          .endOf("day")
                          .toDate()
                    : filterEndDate,
            poolIds: poolGroups,
            workType: workType,
            integrationType: integrationType
        }),
        dependencies: [recall],
        initialCall: false,
        waitForResponse: true
    });

    const [
        upperGroupsByCompanyAndCityLoading,
        upperGroupsByCompanyAndCity,
        setUpperGroupsByCompanyAndCity,
        setUpperGroupsByCompanyAndCityLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId: filters.companies.join("."),
            selectedCities: selectedCities.join(",")
        }),
        dependencies: [selectedCities, filters.companies],
        initialCall: true
    });

    const [isPoolLoading, pools] = useFetch({
        fetchFn: stores.poolStore.getPoolList.bind(null, {
            limit: 1000,
            page: 1,
            selectedCities: selectedCities.join(","),
            integrationType: integrationType
        }),
        initialCall: true,
        dependencies: [selectedCities, integrationType]
    });

    useEffect(() => {
        if (isFocused) {
            if (
                filters.orderType === "current" ||
                filters.orderType === "burningOrders" ||
                filters.orderType === "inQueue" ||
                filters.orderType === "inSlot" ||
                filters.orderType === "inPrep"
            ) {
                let timeout = null;
                timeout = setTimeout(() => {
                    setRecall(!recallRef.current);
                }, 5000);
                return () => clearTimeout(timeout);
            }
        }
    }, [recall, filters.orderType, isFocused]);

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        const urlPage = urlSearchParams.get("page");

        let urlCity = urlSearchParams.get("city");
        let urlRegionGroups = urlSearchParams.get("regionGroups");
        let urlCompanies = urlSearchParams.get("companies");
        const urlSearchText = urlSearchParams.get("searchText");
        const urlOrderType = urlSearchParams.get("orderType");
        const urlCourierText = urlSearchParams.get("courierText");

        if (urlPage) {
            const page = parseInt(urlPage);
            if (!isNaN(page)) setCurrentPage(page);
        }

        if (urlCity) {
            const notValid = !isConstantExist(cities, urlCity);
            if (!notValid) {
                stores.meStore.map.filters.city = urlCity;
            }
        }

        if (urlRegionGroups) {
            urlRegionGroups = urlRegionGroups.split("-");
            const notValid = urlRegionGroups.find(
                reg => !isConstantExist(filters.regionGroups, reg)
            );
            if (!notValid) {
                stores.meStore.orderPage.filters.regionGroups = urlRegionGroups;
            }
        }
        if (urlCompanies) {
            urlCompanies = urlCompanies.split("-");
            stores.meStore.orderPage.filters.companies = urlCompanies;
        }

        if (urlSearchText) {
            setSearchText(urlSearchText);
            setText(urlSearchText);
        }
        if (urlOrderType) {
            stores.meStore.orderPage.filters.orderType = urlOrderType;
        }
        if (urlCourierText) {
            setCourierSearchText(urlCourierText);
            setCourierText(urlCourierText);
        }
    }, []);

    useEffect(() => {
        setSortField(field);
        setUrl();
    }, [
        currentPage,
        selectedCities,
        filters.regionGroups,
        filters.companies,
        searchText,
        courierSearchText,
        filters.orderType,
        field.asc,
        filters.deliveryStatusOverrides
    ]);

    const handleDateChange = value => {
        setCurrentPage(1);
        if (!value || value.length === 0) {
            setSelectedDateRange([]);
        } else {
            setSelectedDateRange(value);
        }
    };

    const setUrl = () => {
        let path = history.location.pathname;
        if (currentPage) {
            path += "?page=" + currentPage;
        }
        if (selectedCities.length) {
            path += "&selectedCities=" + selectedCities.join(",");
        }
        if (filters.regionGroups.length) {
            path += "&regionsGroups=" + filters.regionGroups?.join("-");
        }
        if (filters.companies.length) {
            path += "&companies=" + filters.companies?.join("-");
        }
        if (searchText) {
            path += "&searchText=" + searchText;
        }
        if (filters.orderType) {
            path += "&orderType=" + filters.orderType;
        }
        if (selectedCities.length > 0) {
            path += "&selectedCities=" + selectedCities.join(",");
        }

        history.push(path);
    };
    const changeTab = value => {
        setCurrentPage(1);
        stores.meStore.orderPage.filters.orderType = value;
        stores.meStore.orderPage.filters.deliveryStatusOverrides = [];
        stores.meStore.orderPage.filters.regionGroups = [];
        stores.meStore.orderPage.filters.upperGroup = "";
        stores.meStore.orderPage.filters.dateRange = [];
    };
    return useObserver(() => (
        <React.Fragment>
            <Row type="flex" justify="space-between" data-testid="filterCard">
                <Col>
                    <Tabs activeKey={filters.orderType} onChange={changeTab}>
                        <TabPane
                            tab={t("filters.orderStatus.activeOrders", {ns: "orderPage"})}
                            key="current"
                            data-testid="orderStatusSelect_tabPane"
                        />
                        <TabPane
                            tab={t("filters.orderStatus.completedOrders", {ns: "orderPage"})}
                            key="past"
                            data-testid="orderStatusSelect_tabPane"
                        />
                        <TabPane
                            tab={t("filters.orderStatus.inQueueOrders", {ns: "orderPage"})}
                            key="inQueue"
                            data-testid="orderStatusSelect_tabPane"
                        />
                        <TabPane
                            tab={t("filters.orderStatus.slotOrders", {ns: "orderPage"})}
                            key="inSlot"
                            data-testid="orderStatusSelect_tabPane"
                        />
                        <TabPane
                            tab={t("filters.orderStatus.inPreparationOrders", {ns: "orderPage"})}
                            key="inPrep"
                            data-testid="orderStatusSelect_tabPane"
                        />
                        {stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                            <TabPane
                                tab={
                                    <Col style={{justifyContent: "center", alignItems: "center"}}>
                                        {t("filters.orderStatus.burningOrders", {ns: "orderPage"})}{" "}
                                        <WarningOutlined />
                                    </Col>
                                }
                                key="burningOrders"
                                data-testid="orderStatusSelect_tabPane"
                            />
                        ) : (
                            <></>
                        )}
                    </Tabs>
                </Col>
                <Col span={24}>
                    <Row type="flex" gutter={[20, 20]}>
                        {filters.orderType !== "inQueue" &&
                            filters.orderType !== "inSlot" &&
                            filters.orderType !== "inPrep" && (
                                <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                    <h5>{t("fields.deliveryStatus", {ns: "common"})}</h5>
                                    <SelectField
                                        value={filters.deliveryStatusOverrides}
                                        onChange={value => {
                                            setCurrentPage(1);
                                            stores.meStore.orderPage.filters.deliveryStatusOverrides = value;
                                        }}
                                        mode="multiple"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        data-testid="deliveryStatusSelect"
                                    >
                                        {deliveryStatuses.map(deliveryStatusObj => {
                                            if (deliveryStatusObj.type === filters.orderType) {
                                                return (
                                                    <SelectField.Option
                                                        key={deliveryStatusObj.value}
                                                        value={deliveryStatusObj.value}
                                                        data-testid="deliveryStatusSelect_option"
                                                    >
                                                        {t(
                                                            `${deliveryStatusObj.translation_key}.shortName`,
                                                            {
                                                                ns: "deliveryStatuses"
                                                            }
                                                        )}
                                                    </SelectField.Option>
                                                );
                                            }
                                        })}
                                    </SelectField>
                                </Col>
                            )}
                        {!stores.meStore.checkRole("isCompanyUser") && (
                            <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                <CompanyFilter
                                    showPool={false}
                                    value={filters.companies}
                                    onChange={value => {
                                        setCurrentPage(1);
                                        stores.meStore.orderPage.filters.companies = value;
                                        stores.meStore.orderPage.filters.selectedSubCompanies = [];
                                    }}
                                    mode="multiple"
                                    placeholder={t("filters.addFilter", {ns: "common"})}
                                />
                            </Col>
                        )}
                        <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                            <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                            <SelectField
                                allowClear
                                data-testid="subCompanySelect"
                                value={filters.selectedSubCompanies}
                                mode="multiple"
                                showSearch
                                filterOption={(input, option) =>
                                    option.props.children
                                        .toLowerCase()
                                        .indexOf(input.toLowerCase()) >= 0 ||
                                    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >=
                                        0
                                }
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                onChange={value => {
                                    filters.selectedSubCompanies = value;
                                }}
                            >
                                {subCompanies.rows &&
                                    subCompanies?.rows.map(subCompany => (
                                        <SelectField.Option
                                            key={subCompany._id}
                                            value={subCompany._id}
                                            data-testid="subCompanySelect_option"
                                        >
                                            {subCompany.name}
                                        </SelectField.Option>
                                    ))}
                            </SelectField>
                        </Col>
                        {!stores.meStore.checkRole("isCompanyUser") && (
                            <>
                                {/* <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                    <CityFilter
                                        value={stores.meStore.map.filters.city}
                                        onChange={value => {
                                            setCurrentPage(1);
                                            stores.meStore.map.filters.city = value;
                                            stores.meStore.orderPage.filters.regionGroups = [];
                                            stores.meStore.orderPage.filters.upperGroup = "all";
                                            stores.meStore.orderPage.filters.selectedSubCompanies = [];
                                        }}
                                    />
                                </Col> */}
                                <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                    <CityFilter
                                        value={selectedCities}
                                        onChange={value => setSelectedCities(value)}
                                        mode="multiple"
                                    />
                                </Col>
                            </>
                        )}
                        <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                            <h5>Kurye Adı</h5>
                            <Input.Search
                                value={courierText}
                                onSearch={value => {
                                    setCurrentPage(1);
                                    setCourierSearchText(value);
                                }}
                                onChange={event => {
                                    setCourierText(event.target.value);
                                }}
                            />
                        </Col>
                        {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                            <>
                                <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                                    <h5>Sipariş türleri</h5>
                                    <SelectField
                                        data-testid="workType"
                                        value={workType}
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        onChange={value => setWorkType(value)}
                                        allowClear
                                    >
                                        <SelectField.Option key={"all"} value={"all"}>
                                            Tümü
                                        </SelectField.Option>
                                        <SelectField.Option key={"pool"} value={"pool"}>
                                            Havuz
                                        </SelectField.Option>
                                        <SelectField.Option key={"dedicated"} value={"dedicated"}>
                                            Sabit
                                        </SelectField.Option>
                                    </SelectField>
                                </Col>
                                {workType === "pool" ? (
                                    <>
                                        <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                                            <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                                            <SelectField
                                                mode="multiple"
                                                data-testid="poolGroupSelect"
                                                value={poolGroups}
                                                placeholder={t("filters.addFilter", {ns: "common"})}
                                                onChange={value => setPoolGroups(value)}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    normalizeText(option.props.children).indexOf(
                                                        normalizeText(input)
                                                    ) >= 0
                                                }
                                                optionFilterProp="children"
                                            >
                                                {pools?.data
                                                    ? pools?.data.map(pool => (
                                                          <SelectField.Option
                                                              key={pool._id}
                                                              value={pool._id}
                                                              data-testid="poolGroups_option"
                                                          >
                                                              {pool.name}
                                                          </SelectField.Option>
                                                      ))
                                                    : ""}
                                            </SelectField>
                                        </Col>
                                        <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                                            <h5>
                                                {t("filters.integrationType", {
                                                    ns: "common"
                                                })}
                                            </h5>
                                            <PoolIntegrationTypeFilter
                                                value={integrationType}
                                                onChange={value => setIntegrationType(value)}
                                            />
                                        </Col>
                                    </>
                                ) : null}
                                <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                    <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                                    <SelectField
                                        value={filters.upperGroup}
                                        onChange={value => {
                                            setCurrentPage(1);
                                            stores.meStore.orderPage.filters.upperGroup = value;
                                            stores.meStore.orderPage.filters.regionGroups = [];
                                        }}
                                        disabled={!selectedCities.length}
                                        notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        optionFilterProp="children"
                                        data-testid="upperGroupSelect"
                                        filterOption={(input, option) =>
                                            option.children
                                                .toString()
                                                .toLocaleLowerCase("tr-TR")
                                                .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                                        }
                                        filterSort={(optionA, optionB) =>
                                            optionA.children
                                                .toString()
                                                .toLocaleLowerCase("tr-TR")
                                                .localeCompare(
                                                    optionB.children
                                                        .toString()
                                                        .toLocaleLowerCase("tr-TR")
                                                )
                                        }
                                    >
                                        <SelectField.Option value="all">
                                            {t("filters.all", {ns: "common"})}
                                        </SelectField.Option>
                                        {upperGroupsByCompanyAndCity.map(upperGroup => (
                                            <SelectField.Option
                                                key={upperGroup.group}
                                                value={upperGroup.group}
                                                data-testid="upperGroupSelect_option"
                                            >
                                                {upperGroup.group}
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </Col>

                                <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                    <h5>{t("fields.regionGroup", {ns: "common"})}</h5>
                                    <SelectField
                                        value={filters.regionGroups}
                                        onChange={(value, children) => {
                                            setCurrentPage(1);
                                            stores.meStore.orderPage.filters.regionGroups = value;
                                        }}
                                        disabled={
                                            !selectedCities.length || filters.upperGroup === ""
                                        }
                                        notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                                        mode="multiple"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        optionFilterProp="children"
                                        data-testid="regionGroupSelect"
                                        filterOption={(input, option) =>
                                            option.children
                                                .toString()
                                                .toLocaleLowerCase("tr-TR")
                                                .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                                        }
                                        filterSort={(optionA, optionB) =>
                                            optionA.children
                                                .toString()
                                                .toLocaleLowerCase("tr-TR")
                                                .localeCompare(
                                                    optionB.children
                                                        .toString()
                                                        .toLocaleLowerCase("tr-TR")
                                                )
                                        }
                                    >
                                        {upperGroupsByCompanyAndCity
                                            .find(c => c.group == filters.upperGroup)
                                            ?.regiongroups.map(regionGroupItem => (
                                                <SelectField.Option
                                                    key={regionGroupItem._id}
                                                    value={regionGroupItem._id}
                                                    data-testid="regionGroupSelect_option"
                                                >
                                                    {regionGroupItem.name}
                                                </SelectField.Option>
                                            ))}
                                    </SelectField>
                                </Col>
                                {filters.orderType === "past" && (
                                    <Col xs={24} sm={16} md={16} lg={8} xl={6}>
                                        <h5>{t("fields.date", {ns: "common"})}</h5>
                                        <DatePicker.RangePicker
                                            defaultValue={
                                                JSON.stringify(filters.dateRange) ===
                                                JSON.stringify(["", ""])
                                                    ? undefined
                                                    : [
                                                          filters.dateRange.length > 0
                                                              ? moment(filters.dateRange[0])
                                                              : "",
                                                          filters.dateRange.length > 0
                                                              ? moment(filters.dateRange[1])
                                                              : ""
                                                      ]
                                            }
                                            onChange={handleDateChange}
                                            value={
                                                selectedDateRange && selectedDateRange.length === 2
                                                    ? selectedDateRange
                                                    : undefined
                                            }
                                            placeholder={[
                                                t("fields.startDate", {ns: "common"}),
                                                t("fields.endDate", {ns: "common"})
                                            ]}
                                            style={{width: "100%"}}
                                            allowClear
                                        />
                                    </Col>
                                )}
                                <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                                    <h5>{t("filters.search", {ns: "common"})}</h5>
                                    <Input.Search
                                        value={text}
                                        data-testid="searchInput"
                                        onSearch={value => {
                                            setCurrentPage(1);
                                            setSearchText(value);
                                        }}
                                        onChange={event => {
                                            setText(event.target.value);
                                        }}
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                </Col>
            </Row>
            <OrderTable
                loading={orderListLoading}
                data={recallList?.rows || orderList.rows}
                dataCount={recallList?.dataCount || orderList.dataCount}
                orderType={filters.orderType}
                currentPage={currentPage}
                field={field}
                setField={setField}
                setPage={async page => {
                    setCurrentPage(page);
                    history.push("/orders?page=" + page);
                }}
                setRecall={setRecall}
                recallRef={recallRef}
            />
        </React.Fragment>
    ));
};
